import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { store } from './AppSystem/Redux/Store';
import { Provider } from 'react-redux';
import i18n from "./AppSystem/i18n";
import {I18nextProvider} from "react-i18next";
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './Auth/Components/EntraID/authConfig';
import { MsalProvider } from '@azure/msal-react';
import * as Sentry from "@sentry/react";
import ErrorFallback from './Common/Components/ErrorFallBack';

let sentryLockState = null;

function shouldInitSentry() {
  const globalObj = typeof window !== 'undefined' ? window : {};
  const alreadyInitialized = Boolean(globalObj.__lrfSentryInitialized);
  const hasClient = Boolean(Sentry.getClient && Sentry.getClient());

  if (alreadyInitialized || hasClient) {
    globalObj.__lrfSentryInitialized = true;
    return false;
  }

  globalObj.__lrfSentryInitialized = true;
  return true;
}

function applySentryModalStyles() {
  const host = document.getElementById('sentry-feedback');
  const shadowRoot = host?.shadowRoot;

  if (!shadowRoot || shadowRoot.getElementById('sentry-custom-modal-style')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'sentry-custom-modal-style';
  style.textContent = `
    .dialog {
      background-color: rgba(0, 0, 0, 0.45) !important;
    }

    textarea[name="message"],
    .form textarea {
      resize: none !important;
    }

    @media (max-width: 600px) {
      .dialog__content {
        padding: 12px !important;
        gap: 10px !important;
        height: auto !important;
      }

      .form {
        flex: 0 1 auto !important;
        gap: 10px !important;
        overflow: visible !important;
      }

      .form__right {
        justify-content: flex-start !important;
        gap: 10px !important;
        overflow: visible !important;
      }

      .form__top {
        gap: 6px !important;
      }

      .btn-group {
        gap: 6px !important;
        margin-top: 4px !important;
      }
    }
  `;

  const messageTextarea = shadowRoot.querySelector('textarea[name="message"], textarea');
  if (messageTextarea) {
    messageTextarea.setAttribute('rows', '3');
  }

  shadowRoot.appendChild(style);
}

function lockSentryBackgroundScroll() {
  if (typeof window === 'undefined' || sentryLockState) {
    return;
  }

  const body = document.body;
  const html = document.documentElement;
  const scrollY = window.scrollY || window.pageYOffset || 0;

  sentryLockState = {
    scrollY,
    bodyOverflow: body.style.overflow,
    bodyPosition: body.style.position,
    bodyTop: body.style.top,
    bodyWidth: body.style.width,
    bodyOverscrollBehavior: body.style.overscrollBehavior,
    htmlOverflow: html.style.overflow,
    htmlOverscrollBehavior: html.style.overscrollBehavior,
  };

  html.style.overflow = 'hidden';
  html.style.overscrollBehavior = 'none';
  body.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  body.style.width = '100%';
  body.style.overscrollBehavior = 'none';
}

function unlockSentryBackgroundScroll() {
  if (!sentryLockState) {
    return;
  }

  const body = document.body;
  const html = document.documentElement;

  body.style.overflow = sentryLockState.bodyOverflow;
  body.style.position = sentryLockState.bodyPosition;
  body.style.top = sentryLockState.bodyTop;
  body.style.width = sentryLockState.bodyWidth;
  body.style.overscrollBehavior = sentryLockState.bodyOverscrollBehavior;
  html.style.overflow = sentryLockState.htmlOverflow;
  html.style.overscrollBehavior = sentryLockState.htmlOverscrollBehavior;

  window.scrollTo(0, sentryLockState.scrollY);
  sentryLockState = null;
}

//console.log("BUILD CHECK v0.5:", process.env.REACT_APP_SENTRY_RELEASE);

if (shouldInitSentry()) {
  Sentry.init({
    dsn: process.env.NODE_ENV === "development" ? "https://d42f5d2bfbf7e62dbf1c70fe01792674@o4509989862572032.ingest.de.sentry.io/4510006012608592" : "https://529696377f76b418e6a04a8ece8748ad@o4509999819587584.ingest.de.sentry.io/4510006027288657",
    release: process.env.REACT_APP_SENTRY_RELEASE,
    environment: "STAGE",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.feedbackIntegration({
        colorScheme: "light",
        themeLight: {
            accentBackground: "#157553",
            successColor: "#157553",
            errorColor: "#B42318",
          },
        formTitle: "Ge oss din feedback",
        nameLabel: "Namn",
        namePlaceholder: "Ditt namn",
        emailLabel: "Din e-post",
        emailPlaceholder: "Din e-post",
        messageLabel: "Beskrivning",
        isRequiredLabel: "(obligatorisk)",
        messagePlaceholder: "Vad hände eller vad kan vi förbättra?",
        addScreenshotButtonLabel: "Lägg till skärmdump",
        removeScreenshotButtonLabel: "Ta bort skärmdump",
        submitButtonLabel: "Skicka",
        cancelButtonLabel: "Avbryt",
        successMessageText: "Tack! Vi har tagit emot din feedback.",
        //autoInject: false,
        triggerLabel: "Rapportera ett problem",
        useSentryUser: {
          email: "email",
          name: "name",
        },
        onFormOpen: () => {
          applySentryModalStyles();
          lockSentryBackgroundScroll();
        },
        onFormClose: () => {
          unlockSentryBackgroundScroll();
        },
        autoInject: false,
    }),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    beforeSend(event, hint) {
      // if (event.exception) {
      //   Sentry.showReportDialog({
      //     eventId: event.event_id,
      //   });
      // }
      return event;
    },
    tracePropagationTargets: [
                                "localhost",
                                "lrf-kassan-production-b0fahxcggncshrf7.swedencentral-01.azurewebsites.net",
                                "lrf-kassan-development-hrhqdtetembgfsec.swedencentral-01.azurewebsites.net"
                             ],
    tracesSampleRate: 0.2,
    sendDefaultPii: false,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  });
}

const msalInstance = new PublicClientApplication(msalConfig);
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <MsalProvider instance={msalInstance}>
        <I18nextProvider i18n={i18n}>
          <Provider store={store}>
          {/* <React.StrictMode> */}
            <App />
          {/* </React.StrictMode> */}
          </Provider>
        </I18nextProvider>
    </MsalProvider>
  </Sentry.ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
